import { useCallback, useEffect, useRef, useState } from "react";
import api, { downloadFile, getErrorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Download, RefreshCw } from "lucide-react";

const FLAGS = [
  ["is_group", "Audit Grup / Konsolidasi"], ["first_year", "Tahun Pertama"],
  ["inventory", "Persediaan"], ["fixed_assets", "Aset Tetap"], ["borrowing", "Pinjaman"],
  ["related_parties", "Pihak Berelasi"], ["it_reliance", "Andalan TI"], ["estimates", "Estimasi Signifikan"],
];
const STATUSES = ["Diminta", "Diterima", "Direviu", "Selesai", "Terlambat"];

export default function PBC({ id }) {
  const [items, setItems] = useState([]);
  const [dash, setDash] = useState(null);
  const [flags, setFlags] = useState({ is_group: false, first_year: false });
  const [area, setArea] = useState("all");

  const [error,setError]=useState('');const seq=useRef(0);
  const load = useCallback(async () => {
    const n=++seq.current;setItems([]);setDash(null);setError('');
    try{const [list,summary]=await Promise.all([api.get(`/engagements/${id}/pbc`),api.get(`/engagements/${id}/pbc/dashboard`)]);if(n===seq.current){setItems(list.data);setDash(summary.data);}}
    catch(e){if(n===seq.current){setItems([]);setDash(null);setError(getErrorMessage(e));}}
  },[id]);
  useEffect(() => { load();return()=>{seq.current++;}; }, [load]);

  const generate = async () => {
    try { const { data } = await api.post(`/engagements/${id}/pbc/generate`, flags);
    toast.success(`${data.generated} permintaan dokumen dibuat`);
    await load(); } catch(e) { setItems([]);setDash(null);setError(getErrorMessage(e));toast.error(getErrorMessage(e)); }
  };
  const update = async (rid, body) => { try { await api.patch(`/engagements/${id}/pbc/${rid}`, body); await load(); } catch(e) { seq.current++;setItems([]);setDash(null);setError(getErrorMessage(e));toast.error(getErrorMessage(e)); } };

  const areas = ["all", ...Array.from(new Set(items.map((i) => i.audit_area)))];
  const filtered = area === "all" ? items : items.filter((i) => i.audit_area === area);

  return (
    <div className="space-y-4">
      {error&&<p role="alert">{error}</p>}
      <div className="bg-white border border-border rounded-sm p-4">
        <div className="font-head font-semibold mb-3">Generator Permintaan Dokumen (PBC)</div>
        <div className="flex flex-wrap gap-4 mb-3">
          {FLAGS.map(([k, label]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Checkbox checked={!!flags[k]} onCheckedChange={(v) => setFlags({ ...flags, [k]: !!v })} data-testid={`pbc-flag-${k}`} />{label}</label>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={generate} data-testid="pbc-generate"><RefreshCw size={14} className="mr-1" />Generate Daftar PBC</Button>
          <Button size="sm" variant="outline" onClick={() => downloadFile(`/engagements/${id}/export/pbc`, "PBC.xlsx")} data-testid="pbc-export"><Download size={14} className="mr-1" />Ekspor Excel</Button>
        </div>
      </div>

      {dash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["Total", dash.total], ["Diterima", dash.received], ["Outstanding", dash.outstanding], ["Terlambat", dash.overdue, true], ["Dokumen Hilang", dash.missing, true]].map(([l, v, warn]) => (
            <div key={l} className={`bg-white border rounded-sm p-3 ${warn && v > 0 ? "border-red-300" : "border-border"}`} data-testid={`pbc-stat-${l}`}>
              <div className={`text-xl font-head font-bold tabular ${warn && v > 0 ? "text-red-600" : ""}`}>{v}</div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</div>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">Filter area:</span>
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger className="h-8 w-56" data-testid="pbc-area-filter"><SelectValue /></SelectTrigger>
          <SelectContent>{areas.map((a) => <SelectItem key={a} value={a}>{a === "all" ? "Semua Area" : a}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-border rounded-sm overflow-x-auto">
        <table className="w-full audit-table">
          <thead><tr><th>ID</th><th>Entitas</th><th>Area</th><th>Dokumen</th><th>Wajib</th><th>Jatuh Tempo</th><th>PIC</th><th>Status</th><th>Reviewer</th><th>Versi</th></tr></thead>
          <tbody>
            {filtered.map((i) => (
              <tr key={i.id} data-testid={`pbc-row-${i.id}`}>
                <td className="font-mono text-xs">{i.request_id}</td>
                <td>{i.entity}</td>
                <td className="text-xs">{i.audit_area}</td>
                <td>{i.document}</td>
                <td>{i.mandatory ? <span className="text-[10px] px-1 border rounded-sm bg-amber-100 text-amber-800">Wajib</span> : "-"}</td>
                <td className="font-mono text-xs">{i.due_date}</td>
                <td className="text-xs">{i.client_pic || "-"}</td>
                <td>
                  <Select disabled={i.source_kind==='DOCUMENT_MATCH_EXCEPTION'} value={i.status} onValueChange={(v) => update(i.id, { status: v })}>
                    <SelectTrigger className="h-7 text-xs w-28" data-testid={`pbc-status-${i.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </td>
                <td><Input readOnly={i.source_kind==='DOCUMENT_MATCH_EXCEPTION'} className="h-7 text-xs w-28" defaultValue={i.reviewer} onBlur={(e) => e.target.value !== i.reviewer && update(i.id, { reviewer: e.target.value })} /></td>
                <td className="tabular text-center">{i.version}</td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="text-center text-muted-foreground py-6">Belum ada permintaan. Klik "Generate Daftar PBC".</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
